import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { Class, StudentLessonResult, Question, StudentEntry, User, StudentLessonResultWithMetadata, Difficulty, QuestionSlide, Lesson, Answer, LessonSearchDetails, QuestionOverview, SECTIONS, LessonOverview } from '@all-for-one/shared-types'

/**
 * @module web/util/rtk-api
 * @description This module contains functions used to interact with the backend api
 */

// Use api.localhost in local; switch between prod/staging API domains in hosted envs
const hostname = window.location.hostname
const endpoint = hostname === 'localhost'
	? 'http://api.localhost/'
	: (hostname.includes('staging.')
		? 'https://api.staging.allforonelearn.com/'
		: 'https://api.allforonelearn.com/')


export const api = createApi({
	reducerPath: 'api',
	tagTypes: ['Lesson', 'Class', 'Result', 'UserLessons', 'Assignment'],

	baseQuery: fetchBaseQuery({
		baseUrl: endpoint,
		credentials: 'include',
		prepareHeaders: (headers) => headers,
	}),
	endpoints: (builder) => ({
		// Users
		getUserDetailsRTK: builder.query<User, string>({
			query: (userId) => ({ url: `users/${userId}`, method: 'GET' }),
		}),
	
		// Classes
		getClasses: builder.query<{classes: Class[], nextPageToken: string}, void>({
			query: () => ({ url: 'classes', method: 'GET' }),
			providesTags: [{ type: 'Class', id: 'LIST' }],
		}),
		getClass: builder.query<Class, { classId: string }>({
			query: ({ classId }) => ({ url: `classes/${classId}`, method: 'GET' }),
			providesTags: (result, error, { classId }) => [{ type: 'Class', id: classId }],
		}),
		assignClass: builder.mutation<void, { lessonId: string; classId: string; previousClassId?: string; gradeBand?: string }>({
			query: ({ lessonId, classId, previousClassId, gradeBand }) => ({
				url: `classes/${classId}`,
				method: 'POST',
				body: { lessonId, previousClassId, gradeBand },
			}),
			invalidatesTags: (result, error, { lessonId, classId, previousClassId }) => [
				{ type: 'Lesson', id: lessonId },
				{ type: 'Class', id: classId },
				...(previousClassId ? [{ type: 'Class' as const, id: previousClassId }] : []),
				{ type: 'Assignment' }
			],
		}),
		// requestId is an idempotency key (L1): a double-click or a retry after a
		// timeout returns the SAME class instead of provisioning a second set of
		// student accounts.
		createClass: builder.mutation<{message: string, classId: string}, { name: string; grades: string[]; period: string; subject: string; students: StudentEntry[]; defaultPassword?: string; requestId?: string }>({
			query: ({ name, grades, period, subject, students, defaultPassword, requestId }) => ({
				url: 'classes',
				method: 'POST',
				body: { name, grades, period, subject, students, defaultPassword, requestId },
			}),
			invalidatesTags: [{ type: 'Class', id: 'LIST' }],
		}),
		
		addStudentToClass: builder.mutation<StudentEntry, { classId: string; firstName: string; lastName: string; difficulty: string; grade: string; schoolStudentId: string }>({
			query: ({ classId, firstName, lastName, difficulty, grade, schoolStudentId }) => ({
				url: `classes/${classId}/student`,
				method: 'POST',
				body: { firstName, lastName, difficulty, grade, schoolStudentId },
			}),
			invalidatesTags: (result, error, { classId }) => [
				{ type: 'Class', id: classId },
				{ type: 'Class', id: 'LIST' },
				{ type: 'Result', id: `class-${classId}` }
			],
		}),
		updateStudentDifficulty: builder.mutation<void, { classId: string; studentId: string; difficulty: string }>({
			query: ({ classId, studentId, difficulty }) => ({
				url: `classes/${classId}/students/${studentId}/difficulty`,
				method: 'POST',
				body: { difficulty },
			}),
			async onQueryStarted({ classId, studentId, difficulty }, { dispatch, queryFulfilled }) {
				// Update single class cache
				const patchResult = dispatch(
					api.util.updateQueryData('getClass', { classId }, (draft) => {
						const student = draft.students.find((s) => s.studentId === studentId)
						if (student) student.difficulty = difficulty as Difficulty
					}),
				)
				// Also update the classes list cache (used by assign overlay and copy overlay)
				const patchClasses = dispatch(
					api.util.updateQueryData('getClasses', undefined, (draft) => {
						const targetClass = draft.classes.find((c) => c.classId === classId)
						if (targetClass) {
							const student = targetClass.students.find((s) => s.studentId === studentId)
							if (student) student.difficulty = difficulty as Difficulty
						}
					}),
				)
				try {
					await queryFulfilled
				} catch {
					patchResult.undo()
					patchClasses.undo()
				}
			},
			invalidatesTags: (result, error, { classId }) => [
				{ type: 'Class', id: classId },
				{ type: 'Class', id: 'LIST' },
				{ type: 'Result', id: `class-${classId}` }
			],
		}),
	
		// Lessons
		getLesson: builder.query<Lesson, { lessonId: string }>({
			query: ({ lessonId }) => ({
				url: `lessons/${lessonId}`,
				method: 'GET'
			}),
			providesTags: (result, error, { lessonId }) => [{ type: 'Lesson', id: lessonId }],
			async onQueryStarted(arg, { dispatch, queryFulfilled }) {
				try {
					await queryFulfilled
					// After lesson is successfully fetched, backend has updated lastViewed timestamp
					// Invalidate the lessons list cache so it refetches with fresh timestamps
					dispatch(api.util.invalidateTags([{ type: 'UserLessons', id: 'LIST' }]))
				} catch {
					// Query failed, don't invalidate
				}
			},
		}),
		uploadLesson: builder.mutation<{message: string, lessonId: string}, File>({
			query: (file) => ({
				url: 'lessons',
				method: 'POST',
				body: (() => { const formData = new FormData(); formData.append('lessonDoc', file); return formData })(),
			}),
			invalidatesTags: () => [{ type: 'UserLessons', id: 'LIST' }],
		}),
		/* Erases a child everywhere this system holds them. Returns the server's
		   DeletionReport, which is NOT just a success flag: a 207 means the child
		   is PARTIALLY removed and `failures` names which stores still hold them.
		   Callers must show that rather than reporting a clean deletion. */
		deleteStudentFromClass: builder.mutation<{
			removedFromRoster: boolean
			resultsDeleted: number
			usersRowDeleted: boolean
			loginDeleted: boolean
			loginEventsDeleted: number
			eduqueryResponsesDeleted: number | null
			failures: string[]
		}, { classId: string; studentId: string }>({
			query: ({ classId, studentId }) => ({
				url: `classes/${classId}/students/${studentId}`,
				method: 'DELETE',
			}),
			invalidatesTags: (result, error, { classId }) => [
				{ type: 'Class', id: classId },
				{ type: 'Class', id: 'LIST' },
			],
		}),
		deleteLesson: builder.mutation<{ message: string }, string>({
			query: (lessonId) => ({ url: `lessons/${lessonId}`, method: 'DELETE' }),
			invalidatesTags: (result, error, lessonId) => [
				{ type: 'Lesson', id: lessonId },
				{ type: 'UserLessons', id: 'LIST' }
			],
		}),
		getLessonsSearch: builder.query<{ lessons: LessonSearchDetails[]; nextPageToken?: string }, { classId?: string; pageToken?: string }>({
			query: ({ classId, pageToken } = {}) => {
				const params: string[] = []
				if (pageToken) params.push(`pageToken=${encodeURIComponent(pageToken)}`)
				if (classId) params.push(`classId=${encodeURIComponent(classId)}`)
				const qs = params.length ? `?${params.join('&')}` : ''
				return {
					url: `lessons/user-lessons${qs}`,
					method: 'GET'
				}
			},
			providesTags: (result) => [{ type: 'UserLessons', id: 'LIST' }],
		}),
		// Fetch a single lesson's assignment (classId and name) if any
		getLessonAssignedClass: builder.query<{ classId: string; name: string } | null, { lessonId: string }>({
			query: ({ lessonId }) => ({ url: `lessons/${lessonId}/class`, method: 'GET' }),
			providesTags: (result, error, { lessonId }) => [
				{ type: 'Lesson', id: lessonId },
				{ type: 'Assignment' }
			],
		}),
		generateQuestions: builder.mutation<void, { lessonId: string; gradeLevel?: string; classId?: string; replaceExisting?: boolean; subject?: string }>({
			query: ({ lessonId, gradeLevel, classId, replaceExisting, subject }) => ({
				url: `lessons/${lessonId}/generate-questions`,
				method: 'POST',
				body: { gradeLevel, classId, replaceExisting, subject }
			}),
			invalidatesTags: (result, error, { lessonId }) => [{ type: 'Lesson', id: lessonId }],
		}),
		updateLessonQuestions: builder.mutation<void, { lessonId: string; questions: QuestionSlide[] }>({
			query: ({ lessonId, questions }) => ({
				url: `lessons/${lessonId}/update-questions`,
				method: 'POST',
				body: { questions },
			}),
			invalidatesTags: (result, error, { lessonId }) => [{ type: 'Lesson', id: lessonId }],
		}),
		updateLessonStatus: builder.mutation<void, { lessonId: string; status: string }>({
			query: ({ lessonId, status }) => ({
				url: `lessons/${lessonId}/status`,
				method: 'POST',
				body: { status },
			}),
			invalidatesTags: (result, error, { lessonId }) => [
				{ type: 'Lesson', id: lessonId },
				{ type: 'Lesson', id: 'LIST' }
			],
			async onQueryStarted({ lessonId, status }, { dispatch, queryFulfilled }) {
				// Optimistic update for lesson list
				const patch = dispatch(
					api.util.updateQueryData('getLessonsSearch', {}, (draft) => {
						const lesson = draft.lessons.find(l => l.lessonId === lessonId)
						if (lesson) {
							lesson.status = status
						}
					})
				)
				try {
					await queryFulfilled
				} catch {
					patch.undo()
				}
			},
		}),
		hasResults: builder.query<{ hasResults: boolean }, { lessonId: string }>({
			query: ({ lessonId }) => ({ url: `lessons/${lessonId}/has-results`, method: 'GET' }),
		}),
		duplicateLesson: builder.mutation<{message: string, lessonId: string}, { lessonId: string; targetClassId?: string; newName?: string; regenerateQuestions?: boolean; targetGradeBand?: string }>({
			query: ({ lessonId, targetClassId, newName, regenerateQuestions, targetGradeBand }) => ({
				url: `lessons/${lessonId}/duplicate`,
				method: 'POST',
				body: { targetClassId, newName, regenerateQuestions, targetGradeBand }
			}),
			async onQueryStarted({ lessonId, targetClassId, newName, regenerateQuestions }, { dispatch, getState, queryFulfilled}) {
				// Look up target class name from cached classes query
				const classesState = api.endpoints.getClasses.select(undefined)(getState() as any)
				const targetClass = targetClassId ? classesState?.data?.classes?.find(c => c.classId === targetClassId) : null
				// 1) Optimistically add a temp copy
				const tempId = `temp-${Date.now()}`
				const patchAdd = dispatch(
					api.util.updateQueryData('getLessonsSearch', {}, (draft) => {
						const originalLesson = draft.lessons.find(l => l.lessonId === lessonId)
						if (originalLesson) {
							draft.lessons.push({
								...originalLesson,
								lessonId: tempId,
								name: newName || `${originalLesson.name} (Copy)`,
								lastModified: Date.now(),
								lastViewed: Date.now(),
								hasGradedResults: false,
								assignedClassName: targetClass?.name ?? '-',
								assignedClassId: targetClassId,
								status: regenerateQuestions ? 'Draft' : (originalLesson.status === 'Ready' ? 'Ready' : 'Draft'),
								isDuplicating: true,
							})
						}
					})
				)
				try {
					const { data } = await queryFulfilled
					// 2) Replace tempId with actual lessonId from server
					dispatch(
						api.util.updateQueryData('getLessonsSearch', {}, (draft) => {
							const dup = draft.lessons.find(l => l.lessonId === tempId)
							if (dup) {
								dup.lessonId = data.lessonId
								dup.isDuplicating = false
							}
						})
					)
				} catch (e) {
					// Rollback optimistic add
					patchAdd.undo()
				}
			},
		}),
		renameLesson: builder.mutation<{ message: string }, { lessonId: string; name: string }>({
			query: ({ lessonId, name }) => ({
				url: `lessons/${lessonId}/name`,
				method: 'PATCH',
				body: { name }
			}),
			async onQueryStarted({ lessonId, name }, { dispatch, queryFulfilled }) {
				// Optimistically update the name
				const patchResult = dispatch(
					api.util.updateQueryData('getLessonsSearch', {}, (draft) => {
						const lesson = draft.lessons.find(l => l.lessonId === lessonId)
						if (lesson) {
							lesson.name = name
							lesson.lastModified = Date.now()
						}
					})
				)
				try {
					await queryFulfilled
				} catch {
					patchResult.undo()
				}
			},
		}),
		archiveClass: builder.mutation<{ message: string }, { classId: string }>({
			query: ({ classId }) => ({
				url: `classes/${classId}/archive`,
				method: 'POST',
			}),
			invalidatesTags: [{ type: 'Class', id: 'LIST' }],
		}),
		unarchiveClass: builder.mutation<{ message: string }, { classId: string }>({
			query: ({ classId }) => ({
				url: `classes/${classId}/archive`,
				method: 'DELETE',
			}),
			invalidatesTags: [{ type: 'Class', id: 'LIST' }],
		}),
		getArchivedClasses: builder.query<{ classes: Class[] }, void>({
			query: () => ({ url: 'classes/archived', method: 'GET' }),
			providesTags: [{ type: 'Class', id: 'LIST' }],
		}),
		renameClass: builder.mutation<{ message: string }, { classId: string; name: string }>({
			query: ({ classId, name }) => ({
				url: `classes/${classId}/name`,
				method: 'PATCH',
				body: { name }
			}),
			invalidatesTags: [{ type: 'UserLessons', id: 'LIST' }],
			async onQueryStarted({ classId, name }, { dispatch, queryFulfilled }) {
				// Optimistically update the class name in the list cache
				const patchResult = dispatch(
					api.util.updateQueryData('getClasses', undefined, (draft) => {
						const classItem = draft.classes.find(c => c.classId === classId)
						if (classItem) {
							classItem.name = name
						}
					})
				)
				// Also update the class roster/detail cache so breadcrumb + heading update immediately
				const patchResult2 = dispatch(
					api.util.updateQueryData('getClassResults', { classId }, (draft) => {
						draft.name = name
					})
				)
				try {
					await queryFulfilled
				} catch {
					patchResult.undo()
					patchResult2.undo()
				}
			},
		}),
		refineQuestion: builder.mutation<Question, { lessonId: string; questionId: string; question: Question; feedback: string }>({
			query: ({ lessonId, questionId, question, feedback }) => ({
				url: `lessons/${lessonId}/questions/${questionId}/refine`,
				method: 'POST',
				body: { question, feedback },
			}),
			// No Lesson invalidation: refine is generate-only (writes neither
			// DynamoDB nor EduQuery). The refined content is applied to local
			// state and persisted by the follow-up updateLessonQuestions (DynamoDB)
			// + editQuestionEduquery (EduQuery). Invalidating here forced a stale
			// getLesson refetch that reverted (and could re-persist) the old question.
		}),
		editQuestionEduquery: builder.mutation<void, { lessonId: string; questionId: string; question: Question }>({
			query: ({ lessonId, questionId, question }) => ({
				url: `lessons/${lessonId}/questions/${questionId}/edit`,
				method: 'POST',
				body: { question },
			}),
			// No Lesson invalidation: edit writes EduQuery only, not the DynamoDB
			// questionSlides that getLesson reads. DynamoDB is persisted by the
			// follow-up updateLessonQuestions (which keeps its own invalidation).
		}),
		deleteQuestion: builder.mutation<{ message: string }, { lessonId: string; questionId: string; difficulty: string; slideIndex: number; questionIndex: number }>({
			query: ({ lessonId, questionId, difficulty, slideIndex, questionIndex }) => ({
				url: `lessons/${lessonId}/questions/${questionId}`,
				method: 'DELETE',
				body: { difficulty, slideIndex, questionIndex },
			}),
			invalidatesTags: (result, error, { lessonId }) => [{ type: 'Lesson', id: lessonId }],
		}),
	
		// Results
		getClassResults: builder.query<Class, { classId: string }>({
			query: ({ classId }) => ({ url: `classes/${classId}/students`, method: 'GET' }),
			providesTags: (result, error, { classId }) => [{ type: 'Result', id: `class-${classId}` }],
		}),
		getStudentOverviewResult: builder.query<StudentLessonResult[], { classId: string; studentId: string }>({
			query: ({ classId, studentId }) => ({ url: `classes/${classId}/students/${studentId}`, method: 'GET' }),
			providesTags: (result, error, { classId, studentId }) => [{ type: 'Result', id: `student-${studentId}` }],
		}),
		getStudentLessonResult: builder.query<StudentLessonResultWithMetadata, { classId: string; studentId: string; lessonId: string }>({
			query: ({ classId, studentId, lessonId }) => ({ url: `classes/${classId}/lessons/${lessonId}/students/${studentId}/questions`, method: 'GET' }),
			providesTags: (result, error, { classId, studentId, lessonId }) => [{ type: 'Result', id: `student-${studentId}-lesson-${lessonId}` }],
			async onQueryStarted({ classId, studentId, lessonId }, { dispatch, queryFulfilled, getState }) {
				const { data } = await queryFulfilled
				if (!data?.difficulty) return
				const overviewState = api.endpoints.getStudentOverviewResult.select({ classId, studentId })(getState() as any)
				const overviewLesson = overviewState.data?.find((l: StudentLessonResult) => l.lessonId === lessonId)
				if (!overviewLesson?.difficulty) {
					dispatch(api.util.invalidateTags([{ type: 'Result', id: `student-${studentId}` }]))
				}
			},
		}),

        getLessonOverview: builder.query<LessonOverview, { classId: string; lessonId: string }>({
            query: ({ classId, lessonId }) => ({ url: `classes/${classId}/lessons/${lessonId}`, method: 'GET' }),
			providesTags: (result, error, { classId, lessonId }) => [{ type: 'Result', id: `lesson-${lessonId}` }],
		}),
        getQuestionOverview: builder.query<QuestionOverview, { classId: string; lessonId: string; questionId: string, section?: SECTIONS }>({
            query: ({ classId, lessonId, questionId, section }) => ({ url: `classes/${classId}/lessons/${lessonId}/questions/${questionId}?section=${section}`, method: 'GET' }),
			providesTags: (result, error, { classId, lessonId, questionId }) => [{ type: 'Result', id: `question-${questionId}` }],
		}),
		updateQuestionResult: builder.mutation<{ message: string; overallScore?: number; sectionScores?: Array<{ section: string; score: number }> }, { studentId: string; lessonId: string; questionId: string; score: number; reset?: boolean; classId: string }>({
			query: ({ studentId, lessonId, questionId, score, reset }) => ({
				url: `results/question/${lessonId}/${studentId}/${questionId}`,
				method: 'PATCH',
				body: { score, reset },
			}),
			invalidatesTags: (result, error, { studentId, lessonId, questionId, classId }) => [
				{ type: 'Result', id: `student-${studentId}-lesson-${lessonId}` },
				{ type: 'Result', id: `student-${studentId}` },
				{ type: 'Result', id: `question-${questionId}` },
				{ type: 'Result', id: `lesson-${lessonId}` },
				{ type: 'Result', id: `class-${classId}` },
			],
		}),
		addResult: builder.mutation<{ message: string }, { studentId: string; lessonId: string; answers: Answer[]; classId: string; skipGrading?: boolean }>({
			query: (body) => ({ url: `results`, method: 'POST', body }),
			invalidatesTags: (result, error, { studentId, lessonId, classId }) => [
				{ type: 'Result', id: `student-${studentId}` },
				{ type: 'Result', id: `class-${classId}` },
				{ type: 'Result', id: `lesson-${lessonId}` }
			],
		}),
	})
})

// Export hooks
export const {
	useUploadLessonMutation,
	useGenerateQuestionsMutation,
	useRefineQuestionMutation,
	useGetClassesQuery,
	useAssignClassMutation,
	useUpdateLessonQuestionsMutation,
	useUpdateLessonStatusMutation,
	useEditQuestionEduqueryMutation,
	useGetClassQuery,
	useCreateClassMutation,
	useGetStudentLessonResultQuery,
	useGetQuestionOverviewQuery,
	useUpdateStudentDifficultyMutation,
	useAddStudentToClassMutation,
	useGetUserDetailsRTKQuery,
	useGetStudentOverviewResultQuery,
	useGetClassResultsQuery,
	useGetLessonOverviewQuery,
    useDuplicateLessonMutation,
	useRenameLessonMutation,
	useRenameClassMutation,
    useGetLessonAssignedClassQuery,
	useGetLessonQuery,
	useAddResultMutation,
	useGetLessonsSearchQuery,
	useUpdateQuestionResultMutation,
	useDeleteStudentFromClassMutation,
	useDeleteLessonMutation,
	useHasResultsQuery,
	useArchiveClassMutation,
	useUnarchiveClassMutation,
	useGetArchivedClassesQuery,
	useDeleteQuestionMutation,
} = api